import { Table } from "antd"
import { format } from "date-fns";
import { TextService } from "../../../Services/TextService";
import { WebApiMemberDtoAddMemberObjectDto, WebApiMemberDtoMemberDto, WebApiMemberFieldsDtoMemberUploadFieldDto, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto } from "../../../Repository/eventbookyapi";
import { ColumnsType } from "antd/lib/table";
import { mapMemberStatusToString } from "../../../Mappers/mapGroupToListItem";
import { formatToShortFormat, getCurrentYearAsString, getDateStringFromDate, getPreviousYear, getUpcomingYear, excelSerialToDate, isExcelSerialDate } from "../../../Helpers/DateHelpers";
import { UiControlType } from "../../../Types/UiControlType";
import { MemberFieldTypes } from "../../../Types/MemberFieldTypes";
import { ExclamationCircleOutlined } from "@ant-design/icons";

type SkippedMemberReason = 'duplicate_id_in_file' | 'duplicate_email_in_file' | 'existing_id' | 'existing_email' | 'invalid_email';

interface SkippedMember {
    uniqueId: string;
    email?: string | null;
    reason: SkippedMemberReason;
}

interface UploadMemberPreviewDataProps {
    uploadTemplateFields: WebApiMemberFieldsDtoMemberUploadFieldDto[];
    members: WebApiMemberDtoAddMemberObjectDto[];
    skippedMembers?: SkippedMember[];
}

// Reusable function to render Yes/No as a checkmark or cross
function renderYesNoSymbol(value: any) {
    if (
        value === true ||
        (typeof value === 'string' && value.trim().toLowerCase() === 'true')
    ) {
        return <span style={{ color: 'green' }}>&#10003;</span>; // checkmark
    } else if (
        value === false ||
        (typeof value === 'string' && value.trim().toLowerCase() === 'false')
    ) {
        return <span style={{ color: 'red' }}>&#10007;</span>; // cross
    }
    return value;
}

export const UploadMemberPreviewData = (props: UploadMemberPreviewDataProps) => {

    const GetText = TextService.GetText;

    // Helper to get column title from fieldType or customFieldName
    const getColumnTitle = (field: WebApiMemberFieldsDtoMemberUploadFieldDto) => {
        if (field.customFieldName) return field.customFieldName;
        switch (field.fieldType) {
            case 1: return GetText("MemberList_UniqueIdColumn");
            case 2: return GetText("MemberList_FirstNameColumn");
            case 3: return GetText("MemberList_LastNameColumn");
            case 4: return GetText("MemberList_EmailColumn");
            case 5: return GetText("MemberList_EmailColumn2");
            case 6: return GetText("MemberList_AdressColumn");
            case 7: return GetText("MemberList_PostalAreaColumn");
            case 8: return GetText("MemberList_PostalCodeColumn");
            case 9: return GetText("MemberList_CountryColumn");
            case 10: return GetText("MemberList_PhoneColumn");
            case 11: return GetText("MemberList_TitleColumn");
            case 12: return GetText("MemberList_CompanyColumn");
            case 13: return GetText("MemberList_Extra1Column");
            case 14: return GetText("MemberList_Extra2Column");
            case 15: return GetText("MemberList_Extra3Column");
            case 16: return GetText("MemberList_StatusColumn");
            case 17: return GetText("MemberList_GroupsColumn");
            case 18: return "Betalning år " + getCurrentYearAsString();
            case 19: return GetText("MemberList_EmailNotificationPreferenceColumn");
            case 20: return GetText("MemberList_SortOrderColumn");
            case 21: return GetText("MemberList_PersonalNumberColumn");
            case 22: return GetText("MemberList_PhoneColumn2");
            case 23: return GetText("MemberList_AdressRow2Column");
            case 24: return GetText("MemberList_AdressRow3Column");
            case 25: return GetText("MemberList_JoinedDateColumn");
            case 26: return GetText("MemberList_WebPageColumn");
            case 28: return GetText("MemberList_Membership");
            case 29: return GetText("MemberList_MembershipAddon");
            default: return field.fieldType;
        }
    };

    // Helper to get value from member by fieldType or customFieldName
    const getMemberValue = (member: WebApiMemberDtoAddMemberObjectDto, field: WebApiMemberFieldsDtoMemberUploadFieldDto) => {

        if (field.fieldType == MemberFieldTypes.Payment) {
            if (field.customFieldName?.indexOf(getCurrentYearAsString()) !== -1) {
                return member.paymentAmountCurrentYear;
            }

            if (field.customFieldName?.indexOf(getUpcomingYear(-1).toString()) !== -1) {
                return member.paymentAmountYearMinusOne;
            }

            if (field.customFieldName?.indexOf(getUpcomingYear(-2).toString()) !== -1) {
                return member.paymentAmountYearMinusTwo;
            }

            if (field.customFieldName?.indexOf(getUpcomingYear(1).toString()) !== -1) {
                return member.paymentAmountNextYear;
            }

            return "";
        }

        try {
            if (field.customFieldName && member.customFieldValues) {
                const found = member.customFieldValues.find((f: any) => f.fieldId === field.id);
                if (found) {
                    // If YesNo type, show check/cross
                    if (field.uiControl === UiControlType.YesNo) {
                        return renderYesNoSymbol(found.fieldValue);
                    }
                    if (field.uiControl === UiControlType.Date) {
                        if (found.fieldValue != null && found.fieldValue != "") {
                            // Check if it's an Excel serial number
                            if (isExcelSerialDate(found.fieldValue)) {
                                const date = excelSerialToDate(found.fieldValue);
                                return date ? format(date, "yyyy-MM-dd") : found.fieldValue;
                            }
                            return getDateStringFromDate(found.fieldValue);
                        }
                        return "";
                    }
                    return found.fieldValue;
                }
                return "";
            }
        } catch {
            return "";
        }

        switch (field.fieldType) {
            case 1: return member.uniqueId;
            case 2: return member.firstName;
            case 3: return member.lastName;
            case 4: return member.email;
            case 5: return member.email2;
            case 6: return member.address;
            case 7: return member.postalArea;
            case 8: return member.postalCode;
            case 9: return member.country;
            case 10: return member.phoneNumber;
            case 11: return member.title;
            case 12: return member.company;
            case 13: return member.extra1;
            case 14: return member.extra2;
            case 15: return member.extra3;
            case 16: return member.status !== undefined && member.status !== null ? mapMemberStatusToString(member.status) : "";
            case 17: return member.groups;
            case 19: return renderYesNoSymbol(member.emailNotificationPreference == 1 ? "true" : "false");
            case 21: return member.personalNumber;
            case 22: return member.phoneNumber2;
            case 23: return member.adressRow2;
            case 24: return member.adressRow3;
            case 25: return formatToShortFormat(member.joinedDate);
            case 26: return member.webPage;
            case 28: return member.membership;
            case 29: return member.membershipAddon;
            default: return "";
        }
    };

    const columns: ColumnsType<WebApiMemberDtoAddMemberObjectDto> = Array.isArray(props.uploadTemplateFields)
        ? props.uploadTemplateFields.map((field: WebApiMemberFieldsDtoMemberUploadFieldDto, idx: number) => {
            const isEmailField = field.fieldType === 4 || field.fieldType === 5; // Email or Email2
            const isUniqueIdField = field.fieldType === 1; // UniqueId

            return {
                title: getColumnTitle(field),
                dataIndex: field.customFieldName || field.fieldType,
                key: String(field.customFieldName || field.fieldType),
                render: (_: any, member: WebApiMemberDtoAddMemberObjectDto) => {
                    const value = getMemberValue(member, field);
                    const skippedMember = props.skippedMembers?.find(s => s.uniqueId === member.uniqueId!);

                    if (skippedMember) {
                        // Show warning for email field if it's an email-related error
                        if (isEmailField && (skippedMember.reason === 'invalid_email' ||
                            skippedMember.reason === 'duplicate_email_in_file' ||
                            skippedMember.reason === 'existing_email') && value) {
                            return (
                                <span style={{ color: '#ff4d4f' }}>
                                    <ExclamationCircleOutlined style={{ marginRight: '5px' }} />
                                    <b>{value}</b>
                                </span>
                            );
                        }
                        // Show warning for uniqueId field if it's an ID-related error
                        if (isUniqueIdField && (skippedMember.reason === 'duplicate_id_in_file' ||
                            skippedMember.reason === 'existing_id') && value) {
                            return (
                                <span style={{ color: '#ff4d4f' }}>
                                    <ExclamationCircleOutlined style={{ marginRight: '5px' }} />
                                    <b>{value}</b>
                                </span>
                            );
                        }
                    }

                    return <span><b>{value}</b><br /></span>;
                },
            };
        })
        : [];

    if (!columns.length || !Array.isArray(props.members) || !props.members.length) {
        return null;
    }

    return <Table
        scroll={{ x: "max-content" }}
        columns={columns}
        style={{ marginTop: "20px" }}
        locale={{
            emptyText: GetText("UploadMembers_NoMembersFound")
        }}
        className="admin-table"
        rowKey="Id"
        dataSource={props.members}
        rowClassName={(record) => {
            return props.skippedMembers?.some(s => s.uniqueId === record.uniqueId!) ? 'invalid-email-row' : '';
        }}
    />;



}

export default UploadMemberPreviewData;